/**
 * CGFinder URL and Search Manager
 * Handles URL building, parameter extraction, and search functionality
 */
class CGFinderUrlSearch {
  constructor() {
    // Base URLs and configuration
    this.searchBaseUrl = '/search';
    this.collectionBaseUrl = '/collections';

    // Initialize link interceptor for collections and search
    this.initLinkInterceptor();
  }

  /**
   * Debug method to check CGFinder status
   */
  debugCGFinderStatus() {
  }

  /**
   * Initialize link processing and page URL check on load
   */
  initLinkInterceptor() {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.processPageOnLoad();
        this.initSearchFormInterceptor();
      });
    } else {
      this.processPageOnLoad();
      this.initSearchFormInterceptor();
    }

    // Handle "Clear All Filters" button clicks
    document.addEventListener('click', (event) => {
      const removeLink = event.target.closest('a.active-facets__button');
      if (!removeLink) return;

      const labelSpan = removeLink.querySelector('.active-facets__button-inner');
      if (!labelSpan) return;

      const labelText = labelSpan.textContent.trim().toLowerCase();
      if (labelText.includes('in stock')) {
        event.preventDefault();
        const url = new URL(window.location.href);
        url.searchParams.delete('filter.v.availability');
        window.location.href = url.toString();
      }
    });

    // Re-run updateAllLinks after AJAX content swap (View Results, SET AS VEHICLE)
    this.initAjaxLinkUpdater();
  }

  /**
   * Watch for AJAX content changes and re-apply vehicle filters to new links
   */
  initAjaxLinkUpdater() {
    let debounceTimer = null;
    const debouncedUpdate = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => this.updateAllLinks(), 300);
    };

    // Observe product grid / search results for Shopify AJAX content swaps
    const setupObserver = () => {
      const targets = [
        document.getElementById('product-grid'),
        document.getElementById('ProductGridContainer'),
        document.querySelector('.collection'),
        document.querySelector('.search-results'),
        document.getElementById('main-search-results')
      ].filter(Boolean);

      if (targets.length > 0) {
        const observer = new MutationObserver(debouncedUpdate);
        targets.forEach(target => {
          observer.observe(target, { childList: true, subtree: true });
        });
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupObserver);
    } else {
      setupObserver();
    }

    // Also handle back/forward navigation
    window.addEventListener('popstate', () => {
      setTimeout(() => this.updateAllLinks(), 300);
    });
  }

  /**
   * Intercept search form submissions to include vehicle filter params.
   * Prevents the double-load where search results first show unfiltered,
   * then processPageOnLoad redirects with the vehicle filter.
   */
  initSearchFormInterceptor() {
    // Find all search forms: both <form action="/search"> and <search-form> custom elements
    const searchForms = document.querySelectorAll('form[action="/search"], form[action$="/search"]');

    searchForms.forEach(form => {
      form.addEventListener('submit', (event) => {
        // Get vehicle data from localStorage
        const vehicleData = this.getVehicleData();
        const metaobjectId = this.getVehicleMetaobjectId();

        // If no vehicle selected, let the form submit normally
        if (!metaobjectId && (!vehicleData.yearText || !vehicleData.modelText)) {
          return;
        }

        // Prevent default form submission
        event.preventDefault();

        // Build the search URL with the form's query value
        const formData = new FormData(form);
        const searchQuery = formData.get('q') || '';
        const prefix = formData.get('options[prefix]') || 'last';

        // Build URL with search params + vehicle filter
        const url = new URL('/search', window.location.origin);
        url.searchParams.set('options[prefix]', prefix);
        if (searchQuery) {
          url.searchParams.set('q', searchQuery);
        }

        // Add vehicle filter params
        const searchField = this.getSearchFieldFromStorage();
        if (metaobjectId) {
          url.searchParams.set(`filter.p.m.${searchField}`, metaobjectId);
        } else {
          if (vehicleData.yearText) {
            url.searchParams.set('filter.p.m.custom.year', vehicleData.yearText);
          }
          if (vehicleData.modelText) {
            url.searchParams.set('filter.p.m.custom.model', vehicleData.modelText);
          }
        }

        // Navigate directly with vehicle filter included
        window.location.href = url.toString();
      });
    });
  }

  /**
   * Process page on load - check URL and update all links
   */
  processPageOnLoad() {
    // Check if this is a collection or search page

    // Get vehicle data from localStorage
    const vehicleData = this.getVehicleData();
    const metaobjectId = this.getVehicleMetaobjectId();

    // If no vehicle data, do nothing
    if (!metaobjectId && (!vehicleData.yearText || !vehicleData.modelText)) {
      this.updateAllNotVehicleLinks();
      return;
    }

    // Check if current page URL has vehicle filters
    const currentUrl = new URL(window.location.href);
    const hasVehicleFilter = this.hasVehicleFilterInUrl(currentUrl);

    // If current page doesn't have vehicle filters, reload with them
    if (!hasVehicleFilter && (this.isCollectionPage() || this.isSearchPage())) {
      const updatedUrl = this.addVehicleFilterToUrl(window.location.href, vehicleData);
      window.location.href = updatedUrl;
      return;
    }

    // Update all collection and search links on the page with vehicle filters
    this.updateAllLinks();
  }

  /**
   * Check if URL has vehicle filter parameters
   */
  hasVehicleFilterInUrl(url) {
    const urlObj = typeof url === 'string' ? new URL(url) : url;

    // Check for metaobject ID filters
    for (const [key] of urlObj.searchParams) {
      if (key.startsWith('filter.p.m.vehicle.list')) {
        return true;
      }
    }

    // Check for custom year/model filters
    if (urlObj.searchParams.has('filter.p.m.custom.year') ||
      urlObj.searchParams.has('filter.p.m.custom.model')) {
      return true;
    }

    return false;
  }

  updateAllNotVehicleLinks() {
    const links = document.querySelectorAll('a[href]');
    let updatedCount = 0;

    links.forEach(link => {
      // Skip external links, hash links, javascript links, and blank targets
      if (link.target === '_blank' ||
        link.href.startsWith('#') ||
        link.href.startsWith('javascript:') ||
        link.classList.contains('active-facets__button--all')) {
        return;
      }

      // Only process collection and search links
      if (link.href.includes('/collections/')) {
        const urlObj = new URL(link.href);

        if (urlObj.toString() !== link.href) {
          link.href = urlObj.toString();
          updatedCount++;
        }
      }
    });
  }

  /**
   * Update all collection and search links on the page
   */
  updateAllLinks() {
    const vehicleData = this.getVehicleData();
    const metaobjectId = this.getVehicleMetaobjectId();

    // If no vehicle data, skip
    if (!metaobjectId && (!vehicleData.yearText || !vehicleData.modelText)) {

      return;
    }

    // Query all links on the page
    const links = document.querySelectorAll('a[href]');
    let updatedCount = 0;

    links.forEach(link => {
      // Skip external links, hash links, javascript links, and blank targets
      if (link.target === '_blank' ||
        link.href.startsWith('#') ||
        link.href.startsWith('javascript:') ||
        link.classList.contains('active-facets__button--all')) {
        return;
      }

      // Only process collection and search links
      if (this.isCollectionOrSearchLink(link.href)) {
        const updatedUrl = this.addVehicleFilterToUrl(link.href, vehicleData);
        if (updatedUrl !== link.href) {
          link.href = updatedUrl;
          updatedCount++;
        }
      }
    });

  }

  /**
   * Check if URL is a collection or search link
   */
  isCollectionOrSearchLink(url) {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;

      // Check if it's a collection page
      if (pathname.includes('/collections/')) return true;

      // Check if it's a search page or has search query
      if (pathname.includes('/search') || urlObj.searchParams.has('q')) return true;

      return false;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get vehicle metaobject ID from CGFinder or localStorage
   */
  getVehicleMetaobjectId() {
    const vehicleData = this.getVehicleData();
    return vehicleData?.metaobjectId || null;
  }

  getVehicleData() {
    try {
      if (window.CGFinder && typeof window.CGFinder.getCGFinderDataWithUrlFallback === 'function') {
        return window.CGFinder.getCGFinderDataWithUrlFallback();
      }
    } catch (e) {
      console.debug('CGFinder not ready, falling back to localStorage:', e.message);
    }

    try {
      const stored = JSON.parse(localStorage.getItem('cgfinder-selections') || '{}');
      return {
        yearText: stored['cgfinder-year-select'] || '',
        modelText: stored['cgfinder-model-select'] || '',
        detailsText: stored['cgfinder-details-select'] || 'none',
        metaobjectId: stored['cgfinder-metaobjectId'] || null
      };
    } catch (e) {
      return {
        yearText: '',
        modelText: '',
        detailsText: 'none',
        metaobjectId: null
      };
    }
  }

  getVehicleDetail() {
    const vehicleData = this.getVehicleData();
    return vehicleData.detailsText || 'none';
  }

  /**
   * Check if we have complete vehicle selection (defensive version)
   */
  hasCompleteVehicleSelection() {
    // Try CGFinder method first
    try {
      if (window.CGFinder && typeof window.CGFinder.hasCompleteVehicleSelection === 'function') {
        return window.CGFinder.hasCompleteVehicleSelection();
      }
    } catch (e) {
      console.debug('CGFinder hasCompleteVehicleSelection not available, checking localStorage directly');
    }

    // Fallback: check localStorage directly
    try {
      const stored = JSON.parse(localStorage.getItem('cgfinder-selections') || '{}');
      return stored['cgfinder-year-select'] &&
        stored['cgfinder-model-select'] &&
        stored['cgfinder-details-select'] &&
        stored['cgfinder-metaobjectId'];
    } catch (e) {
      return false;
    }
  }

  /**
   * Add vehicle filter parameter to URL
   */
  addVehicleFilterToUrl(url, vehicleData) {
    try {
      const urlObj = new URL(url);

      // Get search field from cgfinder-search-field in localStorage (e.g., "vehicle.list1")
      const searchField = this.getSearchFieldFromStorage();

      // Check for both 'Choose Option' (displayText) and 'none' (value)
      const hasValidDetails = vehicleData.detailsText &&
        vehicleData.detailsText !== 'Choose Option' &&
        vehicleData.detailsText !== 'none';

      if (vehicleData.metaobjectId) {
        const filterKey = `filter.p.m.${searchField}`;

        // Remove ALL existing vehicle filter params first (prevents duplicates
        // when switching between vehicles with different searchFields)
        const keysToRemove = [];
        for (const [key] of urlObj.searchParams) {
          if (key.startsWith('filter.p.m.vehicle.list')) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => urlObj.searchParams.delete(key));

        // Add new vehicle filter
        urlObj.searchParams.set(filterKey, vehicleData.metaobjectId);

        // Remove custom year and model filters when using metaobjectId
        urlObj.searchParams.delete('filter.p.m.custom.year');
        urlObj.searchParams.delete('filter.p.m.custom.model');
      } else {
        // Only add custom filters when not using metaobjectId
        if (vehicleData.yearText) {
          urlObj.searchParams.set('filter.p.m.custom.year', encodeURIComponent(vehicleData.yearText));
        }

        if (vehicleData.modelText) {
          urlObj.searchParams.set('filter.p.m.custom.model', encodeURIComponent(vehicleData.modelText));
        }
      }

      return urlObj.toString();
    } catch (e) {
      console.warn('URL parsing failed:', e);
      return url;
    }
  }

  /**
   * Get search field from localStorage cgfinder-search-field
   */
  getSearchFieldFromStorage() {
    try {
      const stored = JSON.parse(localStorage.getItem('cgfinder-selections') || '{}');
      const searchField = stored['cgfinder-search-field'];

      // Default to vehicle.list1 if not found
      return searchField || 'vehicle.list1';
    } catch (e) {
      console.warn('Failed to get search field from storage:', e);
      return 'vehicle.list1';
    }
  }

  /**
   * Build search URL with vehicle parameters
   */
  buildSearchUrl(vehicleData = null, categoryValue = null, metaobjectId = null, searchField = 'vehicle.list1') {
    // Get vehicle data if not provided
    if (!vehicleData) {
      vehicleData = this.getVehicleData();
    }

    if (vehicleData.hasOwnProperty('yearOnly')) {
      const baseUrl = `${categoryValue}`;
      const params = new URLSearchParams({
        'sort_by': 'best-selling'
      });
      params.append(`filter.p.m.custom.year`, vehicleData.yearOnly);
      return `${baseUrl}?${params.toString()}`;
    }

    // If category is provided, build collection URL with vehicle filters
    if (categoryValue && metaobjectId) {
      const baseUrl = `${categoryValue}`;
      const params = new URLSearchParams({
        'sort_by': 'best-selling'
      });

      // Check for both 'Choose Option' (displayText) and 'none' (value)
      const hasValidDetails = vehicleData.detailsText &&
        vehicleData.detailsText !== 'Choose Option' &&
        vehicleData.detailsText !== 'none';

      if (metaobjectId) {
        params.set(`filter.p.m.${searchField}`, metaobjectId);
      } else {
        // Add year/model parameters when details is not selected or is 'none'
        if (vehicleData.yearText && vehicleData.modelText) {
          params.set('filter.p.m.custom.year', vehicleData.yearText);
          params.set('filter.p.m.custom.model', vehicleData.modelText);
        }
      }


      return `${baseUrl}?${params.toString()}`;
    }

    // Fallback to original search URL format
    const baseUrl = this.searchBaseUrl;
    const params = new URLSearchParams({
      q: `${vehicleData.yearText} ${vehicleData.modelText} ${vehicleData.detailsText}`,
      vehicle_year: vehicleData.yearText,
      vehicle_model: vehicleData.modelText,
      vehicle_details: vehicleData.detailsText
    });

    // Check if stored selections are empty and add year/model parameters
    const hasStoredData = this.hasStoredVehicleData();

    if (!hasStoredData && vehicleData.yearText && vehicleData.modelText) {
      params.set('year', vehicleData.yearText);
      params.set('model', vehicleData.modelText);
    }

    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Build search URL for collection pages with vehicle parameters
   */
  buildCollectionSearchUrl(vehicleData = null, metaobjectId = null, searchField = 'vehicle.list1') {
    // Get vehicle data if not provided
    if (!vehicleData) {
      vehicleData = this.getVehicleData();
    }

    // Get current URL to preserve collection and existing parameters
    const currentUrl = new URL(window.location.href);
    const baseUrl = currentUrl.pathname; // e.g., /collections/camshaft-timing-gear-components-parts

    // Start with existing search parameters (preserve sort_by, etc.)
    const params = new URLSearchParams(currentUrl.search);

    // If no metaobjectId provided, try to get from vehicleData or extract from current URL
    if (!metaobjectId) {
      metaobjectId = vehicleData.metaobjectId;

      if (!metaobjectId) {
        const extractedData = this.extractVehicleDataFromUrl();
        if (extractedData.metaobjectId) {
          metaobjectId = extractedData.metaobjectId;
          searchField = extractedData.searchField || searchField;
        }
      }
    }

    // Remove any existing vehicle filters to replace them
    const keysToRemove = [];
    for (const [key] of params) {
      if (key.startsWith('filter.p.m.vehicle.list') || key.startsWith('filter.p.m.custom.')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => params.delete(key));

    // If we have metaobject ID and details, use precise filtering
    // Check for both 'Choose Option' (displayText) and 'none' (value)
    const hasValidDetails = vehicleData.detailsText &&
      vehicleData.detailsText !== 'Choose Option' &&
      vehicleData.detailsText !== 'none';

    if (metaobjectId) {
      params.set(`filter.p.m.${searchField}`, metaobjectId);
      params.delete('filter.p.m.custom.year');
      params.delete('filter.p.m.custom.model');
    } else {
      // Add custom year and model filters when metaobject exists but details is 'none'
      if (vehicleData.yearText) {
        params.set('filter.p.m.custom.year', vehicleData.yearText);
      }
      if (vehicleData.modelText) {
        params.set('filter.p.m.custom.model', vehicleData.modelText);
      }
    }

    // Ensure we have a sort_by parameter (default to best-selling if not present)
    if (!params.has('sort_by')) {
      params.set('sort_by', 'best-selling');
    }

    const finalUrl = `${baseUrl}?${params.toString()}`;

    return finalUrl;
  }

  /**
   * Extract vehicle data from current URL parameters
   */
  extractVehicleDataFromUrl() {
    if (typeof window === 'undefined') {
      return { metaobjectId: null, searchField: null, year: null, model: null };
    }

    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams(currentUrl.search);

    let metaobjectId = null;
    let searchField = null;

    // Look for vehicle filter parameters (filter.p.m.vehicle.list1, list2, etc.)
    for (const [key, value] of params) {
      if (key.startsWith('filter.p.m.vehicle.list')) {
        // Extract the search field (e.g., 'vehicle.list1' from 'filter.p.m.vehicle.list1')
        searchField = key.replace('filter.p.m.', '');

        // Decode the metaobject ID (URL encoded GID)
        metaobjectId = decodeURIComponent(value);

        break;
      }
    }

    // Extract year and model parameters
    const year = params.get('year') || null;
    const model = params.get('model') || null;

    return {
      metaobjectId: metaobjectId,
      searchField: searchField,
      year: year,
      model: model
    };
  }

  /**
   * Get current URL search parameters
   */
  getCurrentUrlParams() {
    if (typeof window === 'undefined') return new URLSearchParams();

    return new URLSearchParams(window.location.search);
  }

  /**
   * Update URL with new parameters without page reload
   */
  updateUrlParams(params) {
    if (typeof window === 'undefined') return;

    const currentUrl = new URL(window.location.href);

    // Add/update parameters
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        currentUrl.searchParams.set(key, value);
      } else {
        currentUrl.searchParams.delete(key);
      }
    });

    // Update URL without reload
    window.history.replaceState({}, '', currentUrl.toString());
  }

  /**
   * Check if current page is a collection page
   */
  isCollectionPage() {
    if (typeof window === 'undefined') return false;

    return window.location.pathname.startsWith('/collections/');
  }

  /**
   * Check if current page is a search page
   */
  isSearchPage() {
    if (typeof window === 'undefined') return false;

    return window.location.pathname.startsWith('/search');
  }

  /**
   * Get collection handle from current URL
   */
  getCollectionHandle() {
    if (!this.isCollectionPage()) return null;

    const pathParts = window.location.pathname.split('/');
    return pathParts[pathParts.length - 1] || null;
  }

  /**
   * Build filter URL for specific collection
   */
  buildCollectionFilterUrl(collectionHandle, filters = {}) {
    const baseUrl = `/collections/${collectionHandle}`;
    const params = new URLSearchParams();

    // Add each filter
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params.set(key, value);
      }
    });

    // Default sort if not provided
    if (!params.has('sort_by')) {
      params.set('sort_by', 'best-selling');
    }

    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  }

  /**
   * Parse search query from URL
   */
  parseSearchQuery() {
    const params = this.getCurrentUrlParams();
    return {
      q: params.get('q') || '',
      year: params.get('year') || '',
      model: params.get('model') || '',
      vehicle_year: params.get('vehicle_year') || '',
      vehicle_model: params.get('vehicle_model') || '',
      vehicle_details: params.get('vehicle_details') || ''
    };
  }

  /**
   * Get vehicle filters from URL
   */
  getVehicleFiltersFromUrl() {
    const params = this.getCurrentUrlParams();
    const filters = {};

    // Look for all vehicle list filters
    for (const [key, value] of params) {
      if (key.startsWith('filter.p.m.vehicle.list')) {
        const listNumber = key.replace('filter.p.m.vehicle.list', '');
        filters[`list${listNumber}`] = decodeURIComponent(value);
      }
    }

    return filters;
  }

  /**
   * Check if stored vehicle data exists
   */
  hasStoredVehicleData() {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return false;

      const data = JSON.parse(localStorage.getItem('cgfinder-selections') || '{}');
      return !!(data['cgfinder-year-select'] && data['cgfinder-model-select']);
    } catch (error) {
      console.warn('Error checking stored vehicle data:', error);
      return false;
    }
  }

  /**
   * Create search query string from vehicle data
   */
  createSearchQuery(vehicleData) {
    // Check for both 'Choose Option' (displayText) and 'none' (value)
    const hasValidDetails = vehicleData.detailsText &&
      vehicleData.detailsText !== 'Choose Option' &&
      vehicleData.detailsText !== 'none';

    const parts = [
      vehicleData.yearText,
      vehicleData.modelText,
      hasValidDetails ? vehicleData.detailsText : ''
    ].filter(Boolean);

    return parts.join(' ').trim();
  }

  /**
   * Validate URL format (supports both absolute and relative URLs)
   */
  isValidUrl(url) {
    if (typeof url !== 'string' || url.trim() === '') {
      return false;
    }

    // Check for relative URLs (starts with / or #)
    if (url.startsWith('/') || url.startsWith('#')) {
      return true;
    }

    // Check for absolute URLs
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get base domain from URL
   */
  getBaseDomain(url = null) {
    try {
      const targetUrl = url ? new URL(url) : window.location;
      return `${targetUrl.protocol}//${targetUrl.host}`;
    } catch {
      return '';
    }
  }

  /**
   * Navigate to URL
   */
  navigateTo(url) {
    if (typeof window === 'undefined') return;

    if (this.isValidUrl(url)) {
      window.location.href = url;
    } else {
      console.warn('❌ Invalid URL provided for navigation:', url);
    }
  }

  /**
   * Get debug info about current URL state
   */
  getUrlDebugInfo() {
    if (typeof window === 'undefined') {
      return { message: 'Not in browser environment' };
    }

    return {
      pathname: window.location.pathname,
      search: window.location.search,
      isCollection: this.isCollectionPage(),
      isSearch: this.isSearchPage(),
      collectionHandle: this.getCollectionHandle(),
      searchQuery: this.parseSearchQuery(),
      vehicleFilters: this.getVehicleFiltersFromUrl(),
      extractedVehicleData: this.extractVehicleDataFromUrl()
    };
  }
}

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.CGFinderUrlSearch = CGFinderUrlSearch;
}